import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {
  MintAccessRole,
  MintAuthService,
  MintLogger,
  MintUserGuard,
  MintUserModel,
  MintUserService,
} from '@bryllyant/mint-ngx';

import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SystemService } from '../../../../common/src/lib/services/system.service';
import { AppIdentifier } from '../../../../common/src/lib/types';
import { AlertUtil } from '../../../../common/src/lib/utils/alert-util';

const logger = MintLogger.getLogger('RoleGuard');

@Injectable({ providedIn: 'root' })
export class RoleGuard {
  constructor(
    private readonly authService: MintAuthService,
    private readonly notificationService: NzNotificationService,
    private readonly userService: MintUserService,
    private readonly systemService: SystemService,
    private readonly userGuard: MintUserGuard,
    private readonly router: Router,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const isAdminPortal = route.data.app === AppIdentifier.Admin;
    const expectedRole: MintAccessRole = route.data.role;

    logger.debug('RoleGuard called ...');

    // allow access to verification screen
    if (
      state.url.startsWith('/onboarding/verification') ||
      state.url.startsWith('/onboarding/register-mobile')
    ) {
      return true;
    }

    logger.debug('canActivate(): role expected', expectedRole);

    const authenticated = await this.userGuard.canActivate(route, state);

    if (authenticated) {
      const user: MintUserModel = await this.authService.me();

      if (isAdminPortal) {
        const hasRole = await this.userService.hasRole(user, expectedRole);

        if (!hasRole) {
          await this.systemService.logout(true);

          // avoid exposing user's role
          AlertUtil.notify(this.notificationService, {
            error: `Invalid username or password. Please try again.`,
          });
          return false;
        }
      }

      return true;
    }

    return false;
  }
}
